import { Backend } from "@/backend/base";
import { FlairStorageManager } from "@/backend/firebase/storage/storage-manager";
import {
  PrimaryButtonClassName,
  PrimaryButtonClassNameLoading,
} from "@/components/constants/class-names";
import { ScrollAreaContainer } from "@/components/scroll-area/scroll-area";
import { displayUiMessage } from "@/components/utils/display-message";
import { ImageComponent } from "@/components/utils/image";
import { editorContextStore } from "@/contexts/editor-context";
import { CustomModelDatasetItem } from "@/core/common/types";
import { Assets } from "@/core/controllers/assets";
import { classNames } from "@/core/utils/classname-utils";
import { getColorRGBTupleFromHex } from "@/core/utils/color-utils";
import { getCustomModelDatasetItemThumbnailUrl } from "@/core/utils/custom-model-utils";
import { imageProcessingUtils } from "@/core/utils/image-processing-utils";
import { getHtmlImageElementFromUrlAsync } from "@/core/utils/image-utils";
import { rescaleNumber } from "@/core/utils/number-utils";
import { debugError, debugLog } from "@/core/utils/print-utilts";
import { ShortcutsUtils } from "@/core/utils/shortcuts-utils";
import { getObjectEntries } from "@/core/utils/type-utils";
import * as AspectRatio from "@radix-ui/react-aspect-ratio";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import { PaintBrushCursor } from "components/canvas-frames/paint-brush-cursor";
import { LeftPanelDropdownZIndex } from "components/constants/zIndex";
import { PaintMaskCanvasControllerPaintBrushType } from "components/editor/paint-mask-canvas-controller";
import {
  PaintBrushSizeAdjust,
  PaintMaskCanvasTaskBarButton,
  PaintMaskCanvasToolBarRedoButton,
  PaintMaskCanvasToolBarUndoButton,
  usePaintMaskCanvasController,
} from "components/editor/paint-mask-canvas-controller-utils";
import { PaintBrushIcon } from "components/icons/paint-brush-icon";
import { SimpleSpinner } from "components/icons/simple-spinner";
import LoadingCardStyles from "components/panels/panel-items/components/loading-card.module.css";
import { NumberSlider } from "components/panels/panel-items/components/number-slider";
import { InfoBox } from "components/utils/info-box";
import { Tooltip } from "components/utils/tooltip";
import { EraserIcon, TrashIcon } from "lucide-react";
import React, { useImperativeHandle } from "react";
import {
  imageEditorContainerClassName,
  mainPanelClassName,
  toolbarPanelClassName,
} from "./classnames";
import { CustomModelImageEditorBackButton } from "./custom-model-editor-back-button";
import {
  CustomModelFixTextEditorMode,
  CustomModelFixTextSourceImageOption,
  useCustomModelFixTextContext,
} from "./custom-model-fix-text-context";
import {
  CustomModelImageEditorMode,
  useCustomModelImageEditorContext,
} from "./custom-model-image-editor-context";
import { useCustomModelCompareOutputContext } from "./custom-model-image-output-context";

interface CustomModelFixTextImageEditorCanvasImperativeHandle {
  undo: () => void;
  redo: () => void;
  clearMask: () => void;
  saveCanvas: () => void;
  getCanvasMaskUrl: () => string | undefined;
  checkMaskEmpty: () => boolean;
}

const CustomModelFixTextSourceImageEditor = React.forwardRef(
  function CustomModelFixTextSourceImageEditor(
    {
      children,
      className = "",
      tabIndex = 0,
      brushColor = "#84cc16",
      ...props
    }: React.HTMLAttributes<HTMLDivElement> &
      React.PropsWithChildren & {
        brushColor?: string;
      },
    imperativeRef: React.ForwardedRef<CustomModelFixTextImageEditorCanvasImperativeHandle>,
  ) {
    const {
      sourceImageUrl,
      sourceMaskUrl,
      sourceMaskBrushSize,
      setSourceMaskBrushSize,
      sourceMaskBrushType,
      setSourceMaskBrushType,
    } = useCustomModelFixTextContext();

    const {
      canvasContainerRef,
      canvasRef,
      maskCanvasRef,
      brushCursorRef,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerEnter,
      onPointerLeave,
      onWheel,
      isCanvasMoving,
      isPointerOver,
      isBrushVisible,
      isPointerOverToolbarRef,
      undo,
      redo,
      numRedos,
      numUndos,
      clearMask,
      saveCanvas,
      getCanvasMaskUrl,
      checkMaskEmpty,
    } = usePaintMaskCanvasController({
      imageUrl: sourceImageUrl,
      maskImageUrl: sourceMaskUrl,
      brushSize: sourceMaskBrushSize,
      brushType: sourceMaskBrushType,
      brushColor: "#84cc16",
    });

    useImperativeHandle<
      CustomModelFixTextImageEditorCanvasImperativeHandle,
      CustomModelFixTextImageEditorCanvasImperativeHandle
    >(imperativeRef, () => ({
      undo,
      redo,
      clearMask,
      saveCanvas,
      getCanvasMaskUrl,
      checkMaskEmpty,
    }));

    return (
      <div
        {...props}
        tabIndex={tabIndex}
        ref={canvasContainerRef}
        className={classNames(
          "relative w-full h-full rounded-md overflow-hidden bg-zinc-800/50 focus-visible:outline focus-visible:outline-zinc-500/50",
          className,
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onWheel={onWheel}
        onKeyDown={(e) => {
          const event = e.nativeEvent;
          if (ShortcutsUtils.isCtrlZ(event)) {
            undo();
          } else if (ShortcutsUtils.isCtrlShiftZ(event) || ShortcutsUtils.isCtrlY(event)) {
            redo();
          } else if (ShortcutsUtils.isCtrlS(event) && process.env.NODE_ENV === "development") {
            e.preventDefault();
            saveCanvas();
          }
        }}
      >
        <canvas ref={canvasRef} />
        <canvas
          ref={maskCanvasRef}
          className="absolute left-0 top-0 transition-opacity"
          style={{
            opacity: isCanvasMoving ? 0 : 0.5,
          }}
        />
        <PaintBrushCursor
          ref={brushCursorRef}
          className={
            sourceMaskBrushType === PaintMaskCanvasControllerPaintBrushType.Erase
              ? "bg-transparent"
              : "bg-lime-500/30"
          }
          style={{
            display: isBrushVisible ? "block" : "none",
            opacity: isPointerOver ? 1 : 0,
            width: `${sourceMaskBrushSize || 0}px`,
            height: `${sourceMaskBrushSize || 0}px`,
            zIndex: LeftPanelDropdownZIndex,
          }}
        />
        <div className="absolute left-0 bottom-0 p-2 w-full flex items-center justify-center pointer-events-none">
          <div
            className="relative w-fit px-1 py-1 rounded-lg flex flex-row items-center justify-center justify-items-stretch text-sm bg-zinc-800 shadow-md border border-zinc-700 transition-opacity pointer-events-auto divide-x divide-zinc-700"
            onPointerEnter={() => {
              // eslint-disable-next-line react-compiler/react-compiler
              isPointerOverToolbarRef.current = true;
            }}
            onPointerLeave={() => {
              isPointerOverToolbarRef.current = false;
            }}
          >
            <div className="pr-1 grid grid-rows-1 grid-cols-2 items-center justify-center">
              <PaintMaskCanvasToolBarUndoButton
                isDisabled={numUndos <= 0}
                size={16}
                onClick={() => {
                  undo();
                }}
              />
              <PaintMaskCanvasToolBarRedoButton
                isDisabled={numRedos <= 0}
                size={16}
                onClick={() => {
                  redo();
                }}
              />
            </div>
            <div className="px-1 grid grid-rows-1 grid-cols-2 items-center justify-center gap-1">
              <PaintMaskCanvasTaskBarButton
                isActive={sourceMaskBrushType === PaintMaskCanvasControllerPaintBrushType.Paint}
                contentChildren={
                  <div className="flex flex-col">
                    <div className="text-zinc-300 mb-1">Product mask paint tool</div>
                  </div>
                }
                onClick={() => {
                  setSourceMaskBrushType(PaintMaskCanvasControllerPaintBrushType.Paint);
                }}
              >
                <PaintBrushIcon size={16} brushColor="#84cc16" />
              </PaintMaskCanvasTaskBarButton>
              <PaintMaskCanvasTaskBarButton
                isActive={sourceMaskBrushType === PaintMaskCanvasControllerPaintBrushType.Erase}
                contentChildren={
                  <div className="flex flex-col">
                    <div className="text-zinc-300 mb-1">Product mask erase tool</div>
                  </div>
                }
                onClick={() => {
                  setSourceMaskBrushType(PaintMaskCanvasControllerPaintBrushType.Erase);
                }}
              >
                <EraserIcon size={16} />
              </PaintMaskCanvasTaskBarButton>
            </div>
            <div className="hidden xl:block px-1">
              <PaintBrushSizeAdjust
                brushSize={sourceMaskBrushSize}
                setBrushSize={setSourceMaskBrushSize}
              />
            </div>
            <div className="hidden lg:block pl-1">
              <PaintMaskCanvasTaskBarButton
                contentChildren={
                  <div className="flex flex-col">
                    <div className="text-zinc-300 mb-1">Clear all product mask</div>
                  </div>
                }
                onClick={() => {
                  clearMask();
                }}
              >
                <TrashIcon size={16} />
              </PaintMaskCanvasTaskBarButton>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

const CustomModelFixTextTargetImageEditor = React.forwardRef<
  CustomModelFixTextImageEditorCanvasImperativeHandle,
  React.HTMLAttributes<HTMLDivElement> &
    React.PropsWithChildren & {
      brushColor?: string;
    }
>(function CustomModelFixTextTargetImageEditor(
  { children, className = "", tabIndex = 0, brushColor = "#84cc16", ...props },
  imperativeRef,
) {
  const {
    targetImageUrl,
    targetMaskUrl,
    targetMaskBrushSize,
    setTargetMaskBrushSize,
    targetMaskBrushType,
    setTargetMaskBrushType,
  } = useCustomModelFixTextContext();

  const {
    canvasContainerRef,
    canvasRef,
    maskCanvasRef,
    brushCursorRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerEnter,
    onPointerLeave,
    onWheel,
    isCanvasMoving,
    isPointerOver,
    isBrushVisible,
    isPointerOverToolbarRef,
    undo,
    redo,
    numRedos,
    numUndos,
    clearMask,
    saveCanvas,
    getCanvasMaskUrl,
    checkMaskEmpty,
  } = usePaintMaskCanvasController({
    imageUrl: targetImageUrl,
    maskImageUrl: targetMaskUrl,
    brushSize: targetMaskBrushSize,
    brushType: targetMaskBrushType,
    brushColor,
  });

  useImperativeHandle(imperativeRef, () => ({
    undo,
    redo,
    clearMask,
    saveCanvas,
    getCanvasMaskUrl,
    checkMaskEmpty,
  }));

  return (
    <div
      {...props}
      tabIndex={tabIndex}
      ref={canvasContainerRef}
      className={classNames(
        "relative w-full h-full rounded-md overflow-hidden bg-zinc-800/50 focus-visible:outline focus-visible:outline-zinc-500/50",
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onWheel={onWheel}
      onKeyDown={(e) => {
        const event = e.nativeEvent;
        if (ShortcutsUtils.isCtrlZ(event)) {
          e.preventDefault();
          undo();
        } else if (ShortcutsUtils.isCtrlShiftZ(event) || ShortcutsUtils.isCtrlY(event)) {
          e.preventDefault();
          redo();
        } else if (ShortcutsUtils.isCtrlS(event) && process.env.NODE_ENV === "development") {
          e.preventDefault();
          saveCanvas();
        }
      }}
    >
      <canvas ref={canvasRef} />
      <canvas
        ref={maskCanvasRef}
        className="absolute left-0 top-0 transition-opacity"
        style={{
          opacity: isCanvasMoving ? 0 : 0.5,
        }}
      />
      <PaintBrushCursor
        ref={brushCursorRef}
        className={
          targetMaskBrushType === PaintMaskCanvasControllerPaintBrushType.Erase
            ? "bg-transparent"
            : "bg-lime-500/30"
        }
        style={{
          display: isBrushVisible ? "block" : "none",
          opacity: isPointerOver ? 1 : 0,
          width: `${targetMaskBrushSize}px`,
          height: `${targetMaskBrushSize}px`,
          zIndex: LeftPanelDropdownZIndex,
        }}
      />
      <div className="absolute left-0 bottom-0 p-2 w-full flex items-center justify-center pointer-events-none">
        <div
          className="relative w-fit px-1 py-1 rounded-lg flex flex-row items-center justify-center text-sm bg-zinc-800 shadow-md border border-zinc-700 transition-opacity pointer-events-auto divide-x divide-zinc-700"
          onPointerEnter={() => {
            // eslint-disable-next-line react-compiler/react-compiler
            isPointerOverToolbarRef.current = true;
          }}
          onPointerLeave={() => {
            isPointerOverToolbarRef.current = false;
          }}
        >
          <div className="pr-1 grid grid-rows-1 grid-cols-2 items-center justify-center">
            <PaintMaskCanvasToolBarUndoButton
              isDisabled={numUndos <= 0}
              size={16}
              onClick={() => {
                undo();
              }}
            />
            <PaintMaskCanvasToolBarRedoButton
              isDisabled={numRedos <= 0}
              size={16}
              onClick={() => {
                redo();
              }}
            />
          </div>
          <div className="px-1 grid grid-rows-1 grid-cols-2 items-center justify-center gap-1">
            <PaintMaskCanvasTaskBarButton
              isActive={targetMaskBrushType === PaintMaskCanvasControllerPaintBrushType.Paint}
              contentChildren={
                <div className="flex flex-col">
                  <div className="text-zinc-300 mb-1">Mask paint tool</div>
                </div>
              }
              onClick={() => {
                setTargetMaskBrushType(PaintMaskCanvasControllerPaintBrushType.Paint);
              }}
            >
              <PaintBrushIcon size={16} brushColor="#84cc16" />
            </PaintMaskCanvasTaskBarButton>
            <PaintMaskCanvasTaskBarButton
              isActive={targetMaskBrushType === PaintMaskCanvasControllerPaintBrushType.Erase}
              contentChildren={
                <div className="flex flex-col">
                  <div className="text-zinc-300 mb-1">Mask erase tool</div>
                </div>
              }
              onClick={() => {
                setTargetMaskBrushType(PaintMaskCanvasControllerPaintBrushType.Erase);
              }}
            >
              <EraserIcon size={16} />
            </PaintMaskCanvasTaskBarButton>
          </div>
          <div className="hidden xl:block px-1">
            <PaintBrushSizeAdjust
              brushSize={targetMaskBrushSize}
              setBrushSize={setTargetMaskBrushSize}
            />
          </div>
          <div className="hidden lg:block pl-1">
            <PaintMaskCanvasTaskBarButton
              contentChildren={
                <div className="flex flex-col">
                  <div className="text-zinc-300 mb-1">Clear all mask</div>
                </div>
              }
              onClick={() => {
                clearMask();
              }}
            >
              <TrashIcon size={16} />
            </PaintMaskCanvasTaskBarButton>
          </div>
        </div>
      </div>
    </div>
  );
});

function CustomModelSourceImageOptionGridItem({
  option,
  optionId,
}: {
  optionId: string;
  option: CustomModelFixTextSourceImageOption;
}) {
  const storageManager = editorContextStore((state) => state.storageManager);
  const [imageUrl, setImageUrl] = React.useState("");

  const { sourceImageId, setSourceImageId, setSourceImageUrl, setSourceImageOptions } =
    useCustomModelFixTextContext();

  const isActive = React.useMemo(() => optionId === sourceImageId, [optionId, sourceImageId]);

  const getImageUrl = React.useCallback(async () => {
    if (!storageManager) {
      return "";
    }

    const { imageUrl, storagePath, thumbnailUrl } = option;

    if (imageUrl) {
      return imageUrl;
    } else if (storagePath) {
      const storageImageUrl = await Assets.loadAssetFromPath({
        storageManager,
        path: storagePath,
      });

      if (storageImageUrl) {
        setSourceImageOptions((options) => ({
          ...options,
          [optionId]: {
            ...options[optionId],
            imageUrl: storageImageUrl,
          },
        }));
      }

      return storageImageUrl ?? "";
    } else if (thumbnailUrl) {
      return thumbnailUrl;
    }

    return "";
  }, [optionId, option, setSourceImageOptions, storageManager]);

  React.useEffect(() => {
    const { thumbnailUrl } = option;

    if (thumbnailUrl) {
      setImageUrl(thumbnailUrl);
    } else {
      getImageUrl().then((storageImageUrl) => {
        setImageUrl(storageImageUrl);
      });
    }
  }, [option, getImageUrl]);

  React.useEffect(() => {
    if (isActive) {
      getImageUrl().then((storageImageUrl) => {
        if (storageImageUrl) {
          setSourceImageUrl(storageImageUrl);
        }
      });
    }
  }, [isActive, getImageUrl, setSourceImageUrl]);

  return (
    <div
      className={classNames(
        "rounded overflow-hidden border transition-colors",
        isActive ? "border-lime-500" : "border-zinc-800 hover:border-lime-600",
      )}
      onClick={() => {
        setSourceImageId(optionId);
      }}
    >
      <AspectRatio.Root
        ratio={1}
        className={classNames(
          LoadingCardStyles.Card,
          imageUrl ? "" : LoadingCardStyles.IsLoading,
          "relative items-center justify-center",
        )}
      >
        <ImageComponent src={imageUrl} />
      </AspectRatio.Root>
    </div>
  );
}

async function getSourceImageFromDatasetItem({
  storageManager,
  datasetItem,
}: {
  storageManager: FlairStorageManager;
  datasetItem: CustomModelDatasetItem;
}): Promise<CustomModelFixTextSourceImageOption> {
  const thumbnailUrl = await getCustomModelDatasetItemThumbnailUrl({
    storageManager,
    datasetItem,
  });
  return {
    storagePath: datasetItem.storagePath,
    thumbnailUrl,
  };
}

function CustomModelDatasetGrid() {
  const storageManager = editorContextStore((state) => state.storageManager);
  const dataset = editorContextStore((state) => state.customModelDataset);

  const { sourceImageOptions, setSourceImageId, setSourceImageOptions } =
    useCustomModelFixTextContext();

  React.useEffect(() => {
    if (!storageManager || !dataset) {
      return;
    }

    Promise.all(
      getObjectEntries(dataset).map<Promise<[string, CustomModelFixTextSourceImageOption]>>(
        async ([datasetId, datasetItem]) => {
          const option = await getSourceImageFromDatasetItem({
            storageManager,
            datasetItem,
          });

          return [datasetId, option];
        },
      ),
    )
      .then((optionEntries) => {
        return Object.fromEntries(optionEntries);
      })
      .then((imageOptions) => {
        setSourceImageOptions((prevOptions) => ({
          ...prevOptions,
          ...imageOptions,
        }));

        const firstKey = Object.keys(imageOptions)[0];

        if (firstKey) {
          setSourceImageId(firstKey);
        }
      });
  }, [storageManager, dataset, setSourceImageId, setSourceImageOptions]);

  return (
    <div className="grid grid-cols-2 gap-2 md:gap-1 xl:gap-2">
      {getObjectEntries(sourceImageOptions).map(([optionId, option]) => {
        return (
          <CustomModelSourceImageOptionGridItem
            key={optionId}
            optionId={optionId}
            option={option}
          />
        );
      })}
    </div>
  );
}

function CustomModelImageEditorFixTextStrengthSlider() {
  const { strength, setStrength } = useCustomModelFixTextContext();

  return (
    <NumberSlider
      name={
        <Tooltip
          triggerChildren={
            <div className="mr-0 lg:min-w-[8rem] group flex flex-row items-center gap-2">
              <span className="truncate">Fidelity</span>
              <QuestionMarkCircledIcon className="text-zinc-500 group-hover:text-zinc-300 transition-colors" />
            </div>
          }
          contentChildren={
            <div className="flex flex-col gap-2">
              <div>Fidelity controls how closely the result adheres to the reference image.</div>
              <div>
                The higher the fidelity, the closer the result is going to adhere to the reference
                image, but less details are going to be corrected.
              </div>
            </div>
          }
        />
      }
      value={strength}
      setValue={setStrength}
      min={0}
      max={10}
      step={1}
    />
  );
}

type CustomModelFixTextImageEditorCanvasImperativeHandleRef = {
  current: CustomModelFixTextImageEditorCanvasImperativeHandle | null;
};

function CustomModelImageEditorFixTextPrimaryButton({
  sourceCanvasImperativeHandleRef,
  targetCanvasImperativeHandleRef,
}: {
  sourceCanvasImperativeHandleRef: CustomModelFixTextImageEditorCanvasImperativeHandleRef;
  targetCanvasImperativeHandleRef: CustomModelFixTextImageEditorCanvasImperativeHandleRef;
}) {
  const {
    mode,
    setMode,
    sourceImageUrl,
    targetImageUrl,
    strength = 0.5,
  } = useCustomModelFixTextContext();

  const { setWidth, setHeight, setSourceImageUrl, setOutputImageUrls, setActiveOutputImageIndex } =
    useCustomModelCompareOutputContext();

  const { prediction } = useCustomModelImageEditorContext();

  const { setModes } = useCustomModelImageEditorContext();

  const backend = editorContextStore((state) => state.backend);
  const storageManager = editorContextStore((state) => state.storageManager);
  const handleFixDetails = async () => {
    if (!backend || !storageManager) {
      return;
    }

    if (mode !== CustomModelFixTextEditorMode.Idle) {
      return;
    }

    setMode(CustomModelFixTextEditorMode.Loading);

    try {
      const sourceMaskUrl = sourceCanvasImperativeHandleRef.current?.getCanvasMaskUrl();
      const targetMaskUrl = targetCanvasImperativeHandleRef.current?.getCanvasMaskUrl();

      const certaintyThreshold = rescaleNumber({
        value: strength,
        sourceMin: 0,
        sourceMax: 10,
        targetMin: 0.01,
        targetMax: 0.1,
      });

      const response = await backend.customModelFixDetailsCorrespondence({
        sourceImage: sourceImageUrl,
        sourceMask: sourceMaskUrl,
        targetImage: targetImageUrl,
        targetMask: targetMaskUrl,
        certaintyThreshold,
        sourceCustomModelPredictionId: prediction?.id,
      });

      if (!response.ok) {
        // @ts-expect-error
        displayUiMessage(response.message, "error");

        return;
      }

      if (!response.outputStoragePath) {
        displayUiMessage("Invalid output image. Please try again later.", "error");

        return;
      }

      // Display result image

      const outputImageUrl = await Assets.loadAssetFromPath({
        storageManager,
        path: response.outputStoragePath,
      });

      if (!outputImageUrl) {
        displayUiMessage("Unable to download output image. Please try again later.", "error");

        return;
      }

      // const outputImageUrl = targetImageUrl;

      const outputImageElement = await getHtmlImageElementFromUrlAsync(outputImageUrl);

      const { width = 1, height = 1 } = outputImageElement;

      setWidth(width);
      setHeight(height);
      setSourceImageUrl(targetImageUrl);
      setOutputImageUrls([outputImageUrl]);
      setActiveOutputImageIndex(0);

      setModes((prevModes) => [...prevModes, CustomModelImageEditorMode.CompareOutputs]);
    } catch (error) {
      displayUiMessage("Unknown server error.", "error");

      debugError("Error fixing custom model details correspondence: ", error);
    } finally {
      setMode(CustomModelFixTextEditorMode.Idle);
    }
  };

  return (
    <button
      className={classNames(
        mode === CustomModelFixTextEditorMode.Loading
          ? PrimaryButtonClassNameLoading
          : PrimaryButtonClassName,
        "flex flex-row items-center justify-center gap-2",
      )}
      onClick={() => {
        handleFixDetails();
      }}
    >
      {mode === CustomModelFixTextEditorMode.Loading && (
        <SimpleSpinner width={18} height={18} pathClassName="fill-lime-500" />
      )}
      <span>Fix small details</span>
    </button>
  );
}

function useAutogenerateMaskUrlEffect({
  backend,
  imageUrl,
  setMaskImageUrl,
  color,
  canvasImperativeHandleRef,
}: {
  backend?: Backend;
  imageUrl: string;
  color: string;
  setMaskImageUrl: (value: string | undefined) => void;
  canvasImperativeHandleRef: React.MutableRefObject<CustomModelFixTextImageEditorCanvasImperativeHandle>;
}) {
  React.useEffect(() => {
    if (!backend) {
      return;
    }

    setMaskImageUrl(undefined);

    backend
      .removeBackground({
        imageUrl,
      })
      .then((maskImageUrl) => {
        if (maskImageUrl) {
          return imageProcessingUtils.getColorAlphaMaskImage({
            maskImage: maskImageUrl,
            color: getColorRGBTupleFromHex(color),
            useMaskChannel: 3,
          });
        }
        return undefined;
      })
      .then((maskImageUrl) => {
        // Check if the mask canvas is empty

        const isMaskEmpty = canvasImperativeHandleRef.current?.checkMaskEmpty?.();

        if (isMaskEmpty) {
          setMaskImageUrl(maskImageUrl);
        } else {
          debugLog("Mask is not empty, no need to apply auto-generated mask.");
        }
      });
  }, [backend, imageUrl, color, canvasImperativeHandleRef, setMaskImageUrl]);
}

export function CustomModelImageEditorFixTextEditor() {
  const backend = editorContextStore((state) => state.backend);
  const sourceCanvasImperativeHandleRef =
    React.useRef<CustomModelFixTextImageEditorCanvasImperativeHandle | null>(null);
  const targetCanvasImperativeHandleRef =
    React.useRef<CustomModelFixTextImageEditorCanvasImperativeHandle | null>(null);

  const brushColor = "#84cc16";

  const { sourceImageUrl, setSourceMaskUrl } = useCustomModelFixTextContext();

  useAutogenerateMaskUrlEffect({
    backend,
    color: brushColor,
    imageUrl: sourceImageUrl,
    setMaskImageUrl: setSourceMaskUrl,
    canvasImperativeHandleRef: sourceCanvasImperativeHandleRef,
  });

  return (
    <div className={imageEditorContainerClassName}>
      <div
        className={classNames(
          mainPanelClassName,
          "flex-1 flex flex-col lg:flex-row divide-y lg:divide-x divide-zinc-800",
        )}
      >
        <div className="w-full h-[50%] lg:w-[50%] lg:h-auto">
          <CustomModelFixTextSourceImageEditor
            ref={sourceCanvasImperativeHandleRef}
            tabIndex={0}
            className="rounded-r-none"
            brushColor={brushColor}
          />
        </div>
        <div className="w-full h-[50%] lg:w-[50%] lg:h-auto">
          <CustomModelFixTextTargetImageEditor
            ref={targetCanvasImperativeHandleRef}
            tabIndex={1}
            className="rounded-l-none"
            brushColor={brushColor}
          />
        </div>
      </div>
      <ScrollAreaContainer className={classNames(toolbarPanelClassName)}>
        <div className="flex flex-col items-stretch gap-4">
          <CustomModelImageEditorBackButton />
          <div className="flex flex-col items-stretch gap-4">
            <InfoBox defaultValue="" className="w-full">
              <ol className="list-decimal list-outside pl-5 flex flex-col gap-2 text-lime-200 text-sm leading-relaxed">
                <li>Select a reference image from your dataset below.</li>
                <li>
                  On the left canvas (reference image), paint the details where you want to select.
                </li>
                <li>
                  On the right canvas (generated image), paint the details where you want to fix.
                </li>
                <li>
                  Click "Fix small details" button and wait for the result. The model will try to
                  match the details between the painted areas on the two canvases.
                </li>
                <li>
                  If nothing is changed, try lower the "Fidelity" slider, and paint a larger region
                  on the reference and the generated image.
                </li>
              </ol>
            </InfoBox>
            <CustomModelImageEditorFixTextStrengthSlider />
            <CustomModelImageEditorFixTextPrimaryButton
              sourceCanvasImperativeHandleRef={sourceCanvasImperativeHandleRef}
              targetCanvasImperativeHandleRef={targetCanvasImperativeHandleRef}
            />
          </div>
          <CustomModelDatasetGrid />
        </div>
      </ScrollAreaContainer>
    </div>
  );
}
